import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Typography, Box } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { colors } from "../../Theme/theme";

const SectionLabel = styled(Typography)(() => ({
	fontSize: "0.8rem",
	fontWeight: 700,
	letterSpacing: "0.2em",
	color: colors.accent.main,
	textTransform: "uppercase",
	marginBottom: "20px",
}));

const QuoteText = styled(Typography)(({ theme }) => ({
	fontSize: "1.2rem",
	lineHeight: 1.8,
	color: "rgba(255, 255, 255, 0.88)",
	fontFamily: "Georgia, serif",
	fontStyle: "italic",
	marginBottom: theme.spacing(3),
}));

const AuthorName = styled(Typography)(() => ({
	fontSize: "1.1rem",
	fontWeight: 700,
	color: "#ffffff",
	letterSpacing: "0.03em",
	fontFamily: "'Heebo', sans-serif",
}));

const CaseType = styled(Typography)(() => ({
	fontSize: "0.85rem",
	fontWeight: 600,
	letterSpacing: "0.12em",
	color: colors.accent.main,
	textTransform: "uppercase",
	fontFamily: "'Heebo', sans-serif",
}));

const Dot = styled(motion.div, {
	shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
	width: active ? "20px" : "6px",
	height: "6px",
	borderRadius: "3px",
	backgroundColor: active ? colors.accent.main : "rgba(255, 255, 255, 0.25)",
	cursor: "pointer",
	transition: "width 0.3s ease, background-color 0.3s ease",
}));

const testimonials = [
	{
		id: 1,
		quote: "If I could give David Rhee 100 stars I would. He's literally the best — always returning calls fast, answering my questions, and if I don't understand something he explains it clearly. He got me what I deserved and fought for me.",
		author: "Brittney Babcock",
		case: "Auto Accident",
	},
	{
		id: 2,
		quote: "David is a very good lawyer. He was very helpful getting me the compensation that I needed. His communication and dedication to the case was impressive. I was very happy with his law office.",
		author: "Mark Simma",
		case: "Slip and Fall",
	},
	{
		id: 3,
		quote: "David helped me tremendously throughout my experience seeking justice after a very scary car accident. I was pregnant when the accident occurred — he guided me through every step, kept me informed, and most importantly cared about me and my child's well-being.",
		author: "Michelle Walters",
		case: "Car Accident Settlement",
	},
	{
		id: 4,
		quote: "I'm beyond grateful for David Rhee Injury Law and how much they helped me with my case.",
		author: "Alondra Olivas",
		case: "Personal Injury",
	},
	{
		id: 5,
		quote: "Mr. Rhee was very courteous and friendly. He gave me the best advice and was clear answering my questions.",
		author: "Virginia Llanas",
		case: "Personal Injury",
	},
	{
		id: 6,
		quote: "I've been working with David for a little over a year and it has been the best experience.",
		author: "Tania Barraza",
		case: "Personal Injury",
	},
];

const TestimonialCarousel = () => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });

	useEffect(() => {
		if (!inView) return;
		const interval = setInterval(() => {
			setCurrentIndex((prev) => (prev + 1) % testimonials.length);
		}, 8000);
		return () => clearInterval(interval);
	}, [inView]);

	return (
		<Box ref={ref}>
			<SectionLabel>Client Testimonials</SectionLabel>

			<AnimatePresence mode="wait">
				<motion.div
					key={currentIndex}
					initial={{ opacity: 0, y: 16 }}
					animate={{ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }}
					exit={{ opacity: 0, y: -12, transition: { duration: 0.4, ease: "easeIn" } }}
				>
					<FormatQuoteIcon sx={{ fontSize: "3rem", color: colors.accent.main, opacity: 0.7, mb: 1, display: "block" }} />
					<QuoteText>{testimonials[currentIndex].quote}</QuoteText>
					<AuthorName>{testimonials[currentIndex].author}</AuthorName>
					<CaseType>{testimonials[currentIndex].case}</CaseType>
				</motion.div>
			</AnimatePresence>

			<Box sx={{ display: "flex", gap: "8px", mt: 3, alignItems: "center" }} role="tablist" aria-label="Testimonial navigation">
				{testimonials.map((t, index) => (
					<Dot
						key={index}
						active={currentIndex === index ? 1 : 0}
						onClick={() => setCurrentIndex(index)}
						onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setCurrentIndex(index); } }}
						whileHover={{ scale: 1.2 }}
						role="tab"
						tabIndex={0}
						aria-selected={currentIndex === index}
						aria-label={`Review by ${t.author}`}
						style={{ cursor: "pointer" }}
					/>
				))}
			</Box>
		</Box>
	);
};

export default TestimonialCarousel;
